import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React, { useState } from "react";
import styled from "styled-components";
import { Primary } from "@ui/components/containers/primary";
import { AssetManagerIcon, CameraIcon, DeleteIcon } from "@ui/components/icons";
import { Row } from "@ui/components/layout";
import { LoadingBar } from "@ui/components/loadingBar";
import { AssetBrowserModal } from "@ui/components/media/assetBrowser/assetBrowserModal";
import { TooshareVideoPlayer } from "@ui/components/media/tooshareVideoPlayer";
import { FileUpload } from "@ui/components/mediaEditor/fileUpload";
import { Menu } from "@ui/components/menus/menu";
import { MenuRow } from "@ui/components/menus/menuRow";
import { SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { useModalTools } from "@ui/modals/modalContext";
import { fadeIn } from "@ui/styles/keyframes";
import { gap } from "@ui/styles/utils";
import { useEditorController } from "@ui/pages/lms/courseEditorPage/courseEditorController/editorControllerContext";

type IntroductionEditorProps = React.HTMLAttributes<HTMLDivElement>;

export const IntroductionEditor: React.FC<IntroductionEditorProps> = ({ ...htmlProps }) => {
	const controller = useEditorController();
	const course = useObservable(controller.course);
	const [uploadProgress, setUploadProgress] = useState<number | null>(null);
	const { createModal } = useModalTools();

	return (
		<Container {...htmlProps}>
			{course.introduction && (
				<>
					<StyledTooshareVideo asset={course.introduction} />
					<DeleteImageIcon onClick={() => controller.setIntroduction()}>
						<DeleteIcon />
					</DeleteImageIcon>
				</>
			)}
			<Menu
				renderMenuContent={(close) => (
					<SimpleMenuContent>
						<MenuRow>
							<FileUpload
								type="video"
								onUploadStart={() => {
									setUploadProgress(0);
									close();
								}}
								onUploadProgress={setUploadProgress}
								onUpload={(media) => {
									controller.setIntroduction(media);
									setUploadProgress(null);
								}}
								onError={() => {
									return setUploadProgress(null);
								}}
							>
								{() => (
									<Row gap={5} align="center">
										<CameraIcon /> <div>{I18n.t("course.editor.section.video.new")}</div>
									</Row>
								)}
							</FileUpload>
						</MenuRow>
						<MenuRow
							onClick={() => {
								close();
								createModal((closeModal) => (
									<AssetBrowserModal
										allowedTypes={["video/*"]}
										close={(assets) => {
											closeModal();
											const asset = assets?.get(0);
											if (asset) {
												controller.setIntroduction(asset);
											}
										}}
									/>
								));
							}}
						>
							<AssetManagerIcon /> <span>{I18n.t("asset_manager.title")}</span>
						</MenuRow>
					</SimpleMenuContent>
				)}
			>
				<EditIntroduction>
					{uploadProgress !== null ? (
						<LoadingBar progress={uploadProgress} />
					) : (
						<Row gap={6} align="center">
							<CameraIcon />
							<div>
								{I18n.t(course.introduction ? "course.editor.introduction.edit" : "course.editor.introduction.add")}
							</div>
						</Row>
					)}
				</EditIntroduction>
			</Menu>
		</Container>
	);
};

const Container = styled.div`
	${gap(20)};
	position: relative;
`;

const StyledTooshareVideo = styled(TooshareVideoPlayer)`
	width: 100%;
	.quality-control {
		display: none;
	}
`;

const EditIntroduction = styled(Primary)`
	color: ${({ theme }) => theme.colors.primary};
	font-weight: bold;

	svg {
		path  {
			fill: ${({ theme }) => theme.colors.primary};
		}
	}
`;

const DeleteImageIcon = styled.div`
	position: absolute;
	right: 10px;
	top: 10px;
	cursor: pointer;

	@media (pointer: fine) {
		display: none;
	}

	${Container}:hover & {
		display: block;
	}

	animation: ${fadeIn} 0.2s linear;
	transition: transform 0.2s ease-in-out;
	&:hover {
		transform: rotate(90deg);
	}
`;
